import { Injectable } from '@angular/core';
import { LoggerService } from '@wdpr/ra-angular-logger';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { Facet } from '@finder/shared/services/filter-emitter-service/facet.class';
import { HeightFacet } from '@finder/features/list/components/filters/components/height-slider/heightFacet.class';
import { FilterEmitterService } from '@finder/shared/services/filter-emitter-service/filter-emitter.service';

@Injectable()
export class HeightSliderService {

    facets: HeightFacet[];
    shorterFacets: HeightFacet[];
    facetsGuestsQualifyFor: HeightFacet[];
    anyHeightFacet: Facet;
    selectedFacet: HeightFacet;
    defaultSliderValue: number;
    sliderValue: number;
    enabled = true;
    initialized = false;

    facetsSubject = new Subject<Facet[]>();
    private destroy$ = new Subject<void>();

    constructor(
        private loggerService: LoggerService,
        private filterEmitterService: FilterEmitterService
    ) { }

    initialize(facets: any[], anyHeightFacet: any, shorterFacets: any) {
        this.facets = this.getMappedFacets(facets);
        this.shorterFacets = this.getMappedFacets(shorterFacets);

        this.anyHeightFacet = new Facet(
            anyHeightFacet.urlFriendlyId,
            anyHeightFacet.value,
            false,
            'height'
        );

        this.initialized = true;

        this.facetsChanged()
            .pipe(takeUntil(this.destroy$))
            .subscribe(emittedFacets => {
                const heightFacetIndex = this.facets.findIndex(facet => this.selectedFacet.id === facet.id);

                if (heightFacetIndex >= 0) {
                    this.sliderValue = heightFacetIndex;
                    this.setSelectedFacet();
                }
            });
    }

    /* istanbul ignore next */
    disconnect() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    facetsChanged() {
        return this.facetsSubject.asObservable();
    }

    setSelectedFacet() {
        if (!this.facets || !this.anyHeightFacet) {
            this.loggerService.error('Service must be initialized first');

            return;
        }
        const facet = this.facets[this.sliderValue];
        const facets = this.shorterFacets.concat(this.facets.slice(0, this.sliderValue));

        if (!facet) {
            return;
        }
        // Ignore proccessing if the facet is already set
        if (this.selectedFacet && this.selectedFacet.equal(facet)) {
            return;
        }
        // Check if there is a facet set and clear it
        this.clearSelectedFacet();
        // Enable Slider
        this.enabled = true;
        // Enable new facet end emit
        facet.value = true;
        this.selectedFacet = facet;
        this.facetsGuestsQualifyFor = facets;
        this.updateFacets(this.facetsGuestsQualifyFor, true);
        this.anyHeightFacet.value = true;
        this.filterEmitterService.emitFilter(this.anyHeightFacet);
    }

    clearSelectedFacet() {
        if (!this.selectedFacet) {
            return;
        }
        // Disabled selected facet and emit
        this.selectedFacet.value = false;
        this.updateFacets(this.facetsGuestsQualifyFor, false);
        this.selectedFacet = null;
        // Disable Any Height facet and emit
        this.anyHeightFacet.value = false;
        this.filterEmitterService.emitFilter(this.anyHeightFacet);
    }

    clearSelectedFacetAndValue() {
        this.clearSelectedFacet();
        this.sliderValue = null;
    }

    toggle(value: boolean) {
        this.enabled = value;
        if (!this.selectedFacet) {
            return;
        }
        this.selectedFacet.value = value;
        this.updateFacets(this.facetsGuestsQualifyFor, value);
        this.anyHeightFacet.value = value;
        this.filterEmitterService.emitFilter(this.anyHeightFacet);
    }

    getMappedFacets(facets) {
        return facets.map((facet) => new HeightFacet(
            facet.urlFriendlyId,
            facet.value,
            false,
            facet.height
        ));
    }

    updateFacets(facets, value) {
        facets.forEach(facet => facet.value = value);
        this.facetsSubject.next(facets);
        this.filterEmitterService.emitFilter(this.selectedFacet);
    }
}
